import type { Vector } from "../utils/vector";
import type { DevTestSnapshot } from "../devTesting";
import { Packet, PacketType } from "./packet";

export interface PracticeData {
    readonly type: PacketType.Practice
    readonly balance: number
    readonly collected?: number
    readonly dashCooldownMs?: number
    readonly progress: number
    readonly bots: number
    readonly entry: number
    readonly kills?: number
    readonly damageDone?: number
    readonly damageTaken?: number
    readonly timeAlive?: number
    readonly devTest?: DevTestSnapshot
    readonly players: ReadonlyArray<{ readonly id: number; readonly balance: number; readonly extractionProgress?: number }>
    readonly coins: ReadonlyArray<{ readonly id: number; readonly cents: number; readonly position: Vector; readonly height: number; readonly age: number }>
}

export const PracticePacket = new Packet<PracticeData>(PacketType.Practice, {
    serialize(stream, data) {
        stream.writeUint32(data.balance);
        stream.writeUint32(data.collected ?? 0);
        stream.writeUint16(Math.min(10000, Math.max(0, Math.ceil(data.dashCooldownMs ?? 0))));
        stream.writeUint16(Math.round(data.progress * 65535));
        stream.writeUint8(data.bots);
        stream.writeUint16(data.entry);
        stream.writeUint16(data.kills ?? 0);
        stream.writeUint32(Math.ceil(data.damageDone ?? 0));
        stream.writeUint32(Math.ceil(data.damageTaken ?? 0));
        stream.writeUint32(data.timeAlive ?? 0);
        stream.writeBooleanGroup(!!data.devTest);
        if (data.devTest) {
            const dev = data.devTest;
            stream.writeBooleanGroup(dev.invincible, dev.unlimitedAmmo, dev.unlimitedDash, dev.fastExtract);
            for (const value of [dev.helmet, dev.vest, dev.backpack, dev.scope, dev.weapon]) stream.writeUint8(value);
        }
        stream.writeArray(data.players, player => {
            stream.writeUint16(player.id);
            stream.writeUint32(player.balance);
            stream.writeUint8(Math.round(Math.max(0, Math.min(1, player.extractionProgress ?? 0)) * 255));
        }, 1);
        stream.writeArray(data.coins, coin => {
            stream.writeUint32(coin.id);
            stream.writeUint32(coin.cents);
            stream.writePosition(coin.position);
            stream.writeFloat(coin.height, 0, 16, 2);
            stream.writeUint16(coin.age);
        }, 1);
    },
    deserialize(stream, data) {
        data.balance = stream.readUint32();
        data.collected = stream.readUint32();
        data.dashCooldownMs = stream.readUint16();
        data.progress = stream.readUint16() / 65535;
        data.bots = stream.readUint8();
        data.entry = stream.readUint16();
        data.kills = stream.readUint16();
        data.damageDone = stream.readUint32();
        data.damageTaken = stream.readUint32();
        data.timeAlive = stream.readUint32();
        if (stream.readBooleanGroup()[0]) {
            const [invincible, unlimitedAmmo, unlimitedDash, fastExtract] = stream.readBooleanGroup();
            data.devTest = { invincible, unlimitedAmmo, unlimitedDash, fastExtract,
                helmet: stream.readUint8(), vest: stream.readUint8(), backpack: stream.readUint8(),
                scope: stream.readUint8(), weapon: stream.readUint8() };
        }
        data.players = stream.readArray(() => ({ id: stream.readUint16(), balance: stream.readUint32(), extractionProgress: stream.readUint8() / 255 }), 1);
        data.coins = stream.readArray(() => ({
            id: stream.readUint32(), cents: stream.readUint32(), position: stream.readPosition(),
            height: stream.readFloat(0, 16, 2), age: stream.readUint16()
        }), 1);
    }
});
